import {
  GAME_ID,
  CURRENT_SAVE_DATA_VERSION,
  REDUX_ACTIONS,
  STAGES,
  ONBOARDING_STAGES,
  SCREENS,
  ORB_POSITIONS,
} from 'a/idiomatica/data/constants';
import { getRandomInt } from 'root/lib/utilityFunctions';

const AVAILABLE_ORB_POSITIONS = [
  [ORB_POSITIONS.CENTER, ORB_POSITIONS.RIGHT],
  [ORB_POSITIONS.LEFT, ORB_POSITIONS.RIGHT],
  [ORB_POSITIONS.LEFT, ORB_POSITIONS.CENTER],
];
const getOrbPosition = roomType => AVAILABLE_ORB_POSITIONS[roomType][getRandomInt(0, 2)];
const defaultRoomType = getRandomInt(0, 3);
const initState = {
  // Curriculum Pointers
  levelIndex: 0,
  questionIndex: 0,
  questionIdsInRound: [],
  questionUuidsInRound: [],
  progressAmount: 0,
  selectedResponse: undefined,
  answerOptions: [],
  stage: STAGES.QUESTION,
  onboardingStage: ONBOARDING_STAGES.FADE_IN,
  onboardingIndex: 0,
  isOnboardingFeedbackMode: false,
  isOnboardingCorrect: true,
  roomType: defaultRoomType,
  orbPosition: getOrbPosition(defaultRoomType),
  correctAnswerCount: 0,
  records: [],
  roundSessionId: null,
  isItemCorrect: false,
};

const roundPlayReducer = (state = initState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    case REDUX_ACTIONS.SET_LEVEL_INDEX:
      return {
        ...state,
        levelIndex: payload,
        onboardingStage: ONBOARDING_STAGES.FADE_IN,
        onboardingIndex: 0,
        isOnboardingFeedbackMode: false,
      };

    case REDUX_ACTIONS.INITIALIZE_ROUND: {
      const roomType = getNextRoomType(state.roomType);
      return {
        ...state,
        questionIdsInRound: payload.questionIdsInRound,
        questionUuidsInRound: payload.questionUuidsInRound,
        progressAmount: state.questionIndex === payload.questionIdsInRound.length ?
          100 : state.questionIndex * (100 / payload.questionIdsInRound.length),
        selectedResponse: undefined,
        stage: STAGES.QUESTION,
        roomType,
        orbPosition: getOrbPosition(roomType),
      };
    }

    case REDUX_ACTIONS.CHANGE_SCREEN: {
      const onboardingScreens = [SCREENS.ONBOARDING, SCREENS.REPEAT_ONBOARDING];
      if (onboardingScreens.includes(payload)) {
        return state;
      }
      return {
        ...state,
        onboardingStage: ONBOARDING_STAGES.FADE_IN,
        onboardingIndex: 0,
        isOnboardingFeedbackMode: false,
      };
    }

    case REDUX_ACTIONS.SELECT_RESPONSE:
      return {
        ...state,
        selectedResponse: payload.response,
      };

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM: {
      const roomType = getNextRoomType(state.roomType);
      const correctAnswerCount = state.correctAnswerCount + (state.isItemCorrect ? 1 : 0);
      const isLastItem = state.questionIndex === state.questionIdsInRound.length - 1;
      const progressAmount = isLastItem ? 100 : state.progressAmount + (100 / state.questionIdsInRound.length);
      const questionIndex = Math.min(state.questionIndex + 1, state.questionIdsInRound.length - 1);
      return {
        ...state,
        correctAnswerCount,
        progressAmount,
        questionIndex,
        isItemCorrect: false,
        stage: STAGES.QUESTION,
        roomType,
        orbPosition: getOrbPosition(roomType),
      };
    }

    case REDUX_ACTIONS.ROUND_END: {
      return {
        ...initState,
      };
    }

    case REDUX_ACTIONS.LOAD_INTERMEDIATE_SAVE:
      return payload.version < CURRENT_SAVE_DATA_VERSION
        ? state
        : {
          ...state,
          questionIndex: payload.startAt,
          records: payload.records,
          correctAnswerCount: payload.correctAnswerCount,
          roundSessionId: payload.roundSessionId,
        };

    case REDUX_ACTIONS.SET_STAGE:
      return {
        ...state,
        stage: payload.stage,
      };

    case REDUX_ACTIONS.SET_NEXT_STAGE:
      return {
        ...state,
        stage: state.stage + 1,
      };

    case REDUX_ACTIONS.SET_PREVIOUS_STAGE:
      return {
        ...state,
        stage: state.stage - 1,
      };

    case REDUX_ACTIONS.SET_ONBOARDING_STAGE:
      return {
        ...state,
        onboardingStage: payload.onboardingStage,
      };

    case REDUX_ACTIONS.SET_NEXT_ONBOARDING_STAGE:
      return {
        ...state,
        onboardingStage: state.onboardingStage + 1,
      };

    case REDUX_ACTIONS.GET_NEXT_ONBOARDING_ITEM:
      return {
        ...state,
        onboardingIndex: state.onboardingIndex + 1,
      };

    case REDUX_ACTIONS.GET_PREVIOUS_ONBOARDING_ITEM:
      return {
        ...state,
        onboardingIndex: state.onboardingIndex - 1,
      };

    case REDUX_ACTIONS.SET_ONBOARDING_FEEDBACK_MODE: {
      if (state.isOnboardingCorrect && payload.shallSwitchToFeedbackMode) {
        return {
          ...state,
          isOnboardingCorrect: false,
          isOnboardingFeedbackMode: payload.shallSwitchToFeedbackMode,
        };
      }
      return {
        ...state,
        isOnboardingFeedbackMode: payload.shallSwitchToFeedbackMode,
      };
    }
    case REDUX_ACTIONS.SET_ANSWER_OPTIONS:
      return {
        ...state,
        answerOptions: payload.answerOptions,
      };

    case REDUX_ACTIONS.REMOVE_ANSWER_OPTION:
      return {
        ...state,
        answerOptions: state.answerOptions.filter(option => option !== payload.option),
      };

    case REDUX_ACTIONS.SET_ITEM_AS_CORRECT:
      return {
        ...state,
        isItemCorrect: true,
      };

    case REDUX_ACTIONS.QUICK_ROUND:
      return {
        ...state,
        questionIndex: state.questionIdsInRound.length - 1,
        correctAnswerCount: payload.pass ? state.questionIdsInRound.length : 0,
      };

    case REDUX_ACTIONS.UPDATE_RECORDS:
      return {
        ...state,
        records: [...state.records, payload],
      };

    default:
      return state;
  }
};

const getNextRoomType = (roomType) => {
  const availableRooms = [0, 1, 2];
  availableRooms.splice(roomType, 1);
  return availableRooms[getRandomInt(0, 2)];
};

export default roundPlayReducer;
